import {
  Card,
  Chip,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MoreVertRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, Link as RRLink } from 'react-router-dom';
import { GetDocumentGroupMasterDataParams } from '../../../api/document-groups';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import {
  DocumentGroup,
  getDocumentGroupsMasterDataAsync
} from './documentGroupSlice';

export default function DocumentGroupView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();

  const sortOptions = [
    { value: 'id', text: t('documentGroupView.sortOptions.createdDate') },
    { value: 'name', text: t('documentGroupView.sortOptions.name') },
    { value: 'order', text: t('documentGroupView.sortOptions.order') }
  ];
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const { masterData: documentGroups, count } = useSelector(
    (state: RootState) => state.documentGroups
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedDocumentGroup, setSelectedDocumentGroup] =
    useState<DocumentGroup | null>(null);
  const [documentGroupParams, setDocumentGroupParams] =
    useState<GetDocumentGroupMasterDataParams>({
      page: 0,
      limit: 30,
      sortBy: 'order',
      orderBy: ORDER.asc,
      disciplineId: null
    });

  useEffect(() => {
    loadingDocumentGroup(documentGroupParams);
  }, []);

  function loadingDocumentGroup(params: GetDocumentGroupMasterDataParams) {
    dispatch(getDocumentGroupsMasterDataAsync(params));
    setDocumentGroupParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingDocumentGroup({
      ...documentGroupParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, documentGroup?: DocumentGroup) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && documentGroup) {
        setSelectedDocumentGroup(documentGroup);
      }
      setOpenOption(open);
    };
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedDocumentGroup?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`document-groups/${selectedDocumentGroup?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('documentGroupView.drawer.editDocumentGroup')} />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('documentGroupView.drawer.deleteDocumentGroup')}
                secondary={t('documentGroupView.drawer.deleteFunctionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('documentGroupView.breadcrumb.masterDataName'),
          masterDataRef: 'document-groups'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('documentGroupView.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('documentGroupView.toolbar.name')}
        params={documentGroupParams}
        sortOptions={sortOptions}
        loadingElement={loadingDocumentGroup}
      />
      {documentGroups.map((documentGroup) => {
        return (
          <Card className={classes.card} key={documentGroup.id}>
            <div className={classes.cardContainer}>
              <Typography>
                <Tooltip
                  title={`${documentGroup.discipline.cfihosName}, Order no.${documentGroup.order}`}
                  arrow
                >
                  <Chip
                    className={classes.chip}
                    component="span"
                    size="small"
                    variant="outlined"
                    label={`${documentGroup.discipline.name} | ${documentGroup.order}`}
                  />
                </Tooltip>
                {documentGroup.name}
              </Typography>
            </div>
            <div>
              <IconButton
                size="small"
                onClick={toggleDrawer(true, documentGroup)}
              >
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / documentGroupParams.limit)}
          shape="rounded"
          page={documentGroupParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`document-groups/create`}
          variant="extended"
          color="primary"
          aria-label={t('documentGroupView.fab.ariaLabel') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('documentGroupView.fab.newDocumentGroup')}
        </Fab>
      </Zoom>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    chip: {
      margin: theme.spacing(0, 0.5, 0, -1)
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    }
  })
);
